/**
 * 阶段审查演出宿主（2026-10-21 茄子令「审查剧情要带背景图」）
 *
 * 形态＝月度执行页同构：顶部公共信息栏（createExecTopBar，2026-10-26 茄子令「审查剧情内嵌进月度执行页，
 * 和行程一样顶部显示信息栏」）＋`.month-full` 的 `.month-scene` 底图＋底部 VN 卡；无周进度点（非逐周内容）。
 * 场地口径＝`FixedEventDef.playOn === 'stageStory'`；底图读事件自己的 `scene` 键（`data/SceneBgs.ts` 一张表）。
 * 两个入口：决策日入场扫描（开局立约、通过回响），以及 `GameLoop.checkStagePass` 判定失败后的显式派发。
 *
 * 失败局的终局顺序（本轮的要害）：本面板把队列演完才调 `gameLoop.resolvePendingStageFail()` 落结算页。
 * 中途刷新时「待演」标记与事件队列都在存档里，读档回来接着演、演完再结，不会一刷新就跳终局。
 */
import type { GameApp, Panel } from '../../app';
import { gameLoop } from '../../systems/GameLoop';
import { saveSystem } from '../../systems/SaveSystem';
import { GameState } from '../../systems/GameState';
import { SCENE_BGS, SceneKey, applyScenePlaceholder, clearScenePlaceholder, loadSceneBgImage, markSceneActive } from '../../data/SceneBgs';
import { PlotPlayer } from '../plotPlayback';
import { VnDialog } from '../VnDialog';
import { MediaPanel } from './MediaPanel';
import { createDiv, createExecTopBar } from '../ui';

export class StageStoryPanel implements Panel {
    readonly element: HTMLElement;

    /** 公共顶栏（与月度执行页 1:1 同构；审查三段不带数值效果，入场刷一次即可） */
    private readonly topBar = createExecTopBar();
    private readonly scene = createDiv('month-scene');
    private readonly sceneImg: HTMLImageElement;
    /** 当前已申请的场景图 URL（防重复加载/过期 onload 回写，同月度执行页） */
    private appliedScene = '';
    private readonly vn = new VnDialog();
    private readonly mediaPanel = new MediaPanel();
    private readonly plotPlayer = new PlotPlayer('stageStory', {
        playLines: (lines, onEnd, ev) => {
            this.applyScene(ev.scene);
            this.vn.play(lines, onEnd);
        },
        showMedia: media => this.mediaPanel.show(media),
        onDrained: () => this.onDrained(),
    });

    constructor(private app: GameApp) {
        const panel = createDiv('panel-root stage-story-root month-full');
        const sceneImg = document.createElement('img');
        sceneImg.className = 'month-scene-img';
        sceneImg.alt = '';
        this.sceneImg = sceneImg;
        this.scene.appendChild(sceneImg);
        panel.appendChild(this.scene);
        panel.appendChild(this.topBar.root);
        panel.appendChild(this.vn.element);
        this.element = panel;

        // media 拍挂外壳而非本面板根：导航条是面板根的兄弟节点，挂根里会被压在底下（同月度执行页的修法）
        this.app.overlayRoot.appendChild(this.mediaPanel.element);
        this.mediaPanel.onDone = () => this.plotPlayer.onMediaDone();
    }

    refresh(): void {
        this.topBar.update(GameState.getInstance());
        if (this.plotPlayer.playNext()) return;
        // 队里没有本宿主的拍＝数据错配态：别把玩家留在空场景上，下一拍交回决策页
        queueMicrotask(() => this.onDrained());
    }

    destroy(): void {
        this.vn.destroy();
    }

    /** 队列排干：先落失败的终局（若有），否则存档后退回决策页 */
    private onDrained(): void {
        if (gameLoop.resolvePendingStageFail()) return;   // 终局已落，app 自己切结算页
        saveSystem.save();                                // 演出交付置的一次性 flag 落盘（立约/回响跨刷新不重演）
        this.app.show('decision');
    }

    /** 贴底图：当前图 high 优先级、decode 后可见，等待期容器显示图主色底；同图不重复贴（与月度执行页共用一份实现） */
    private applyScene(key: SceneKey | undefined): void {
        if (!key) {
            this.appliedScene = '';
            this.sceneImg.style.visibility = 'hidden';
            this.sceneImg.style.opacity = '0';
            this.sceneImg.removeAttribute('src');
            clearScenePlaceholder(this.scene);
            return;
        }
        const url = SCENE_BGS[key];
        if (this.appliedScene === url) return;
        this.appliedScene = url;
        this.sceneImg.style.visibility = 'hidden';
        this.sceneImg.style.opacity = '0';
        applyScenePlaceholder(this.scene, key);
        markSceneActive(url);
        loadSceneBgImage(this.sceneImg, url, ok => {
            if (this.appliedScene !== url) return;
            if (!ok) {
                this.appliedScene = '';   // 图后补/改名后可重试
                this.sceneImg.style.visibility = 'hidden';
                return;
            }
            this.sceneImg.style.visibility = 'visible';
            requestAnimationFrame(() => { if (this.appliedScene === url) this.sceneImg.style.opacity = '1'; });
            setTimeout(() => { if (this.appliedScene === url) clearScenePlaceholder(this.scene); }, 280);
        });
    }
}
